import * as React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import Button from "@src/components/Button";
import toast from "react-hot-toast";
import { useMutation } from "@apollo/client";
import { RequestGuestLoginOTP, GuestLogin } from "@src/graphql/mutations";
import {
  GuestLoginMutation,
  GuestLoginMutationVariables,
  RequestGuestLoginOtpMutation,
  RequestGuestLoginOtpMutationVariables,
} from "@src/@types/api.d";
import { setCookie } from "cookies-next";
import { unstable_batchedUpdates } from "react-dom";

const emailSchema = Yup.object().shape({
  email: Yup.string()
    .email("Enter a valid email")
    .required("Email is required"),
});

const otpSchema = Yup.object().shape({
  otp: Yup.string().required("Verification code is required"),
});

type EmailFormValues = { email: string };
type OtpFormValues = { otp: string };

interface LoginAsGuestModalProps {
  eventUuid: string | undefined;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const LoginAsGuestModal: React.FC<LoginAsGuestModalProps> = ({
  eventUuid,
  setOpen,
}) => {
  const [step, setStep] = React.useState<"email" | "otp">("email");
  const [emailSent, setEmailSent] = React.useState("");

  const emailForm = useForm<EmailFormValues>({
    mode: "onChange",
    defaultValues: { email: "" },
    resolver: yupResolver(emailSchema),
  });

  const otpForm = useForm<OtpFormValues>({
    mode: "onChange",
    defaultValues: { otp: "" },
    resolver: yupResolver(otpSchema),
  });

  const [requestOTP, { loading: requestOTPLoading }] = useMutation<
    RequestGuestLoginOtpMutation,
    RequestGuestLoginOtpMutationVariables
  >(RequestGuestLoginOTP, {
    onCompleted() {
      setStep("otp");
      setEmailSent(emailForm.getValues("email"));
      toast.success(
        <p className="toast">Verification code sent. Check your email.</p>
      );
    },
    onError(error) {
      if (error.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "Something went wrong"}</p>
        );
      }
    },
  });

  const [guestLogin, { loading: guestLoginLoading }] = useMutation<
    GuestLoginMutation,
    GuestLoginMutationVariables
  >(GuestLogin, {
    onCompleted(res) {
      unstable_batchedUpdates(() => {
        setCookie("tsl", res?.guestLogin?.access_token, {
          maxAge: 90 * 60 * 60 * 24,
        });
        setCookie("usI", res?.guestLogin?.user?.id, {
          maxAge: 90 * 60 * 60 * 24,
        });
        setCookie("evd", res?.guestLogin?.refresh_token, {
          maxAge: 90 * 60 * 60 * 24,
        });
      });
      setOpen(false);
      toast.success(<p className="toast">You're logged in.</p>);
      window.location.reload();
    },
    onError(error) {
      if (error.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "Login failed"}</p>
        );
      }
    },
  });

  const onEmailSubmit: SubmitHandler<EmailFormValues> = (data) => {
    if (!eventUuid) return;
    requestOTP({
      variables: {
        eventId: eventUuid,
        email: data.email,
      },
    });
  };

  const onOtpSubmit: SubmitHandler<OtpFormValues> = (data) => {
    if (!eventUuid || !emailSent) return;
    guestLogin({
      variables: {
        input: {
          event_id: eventUuid,
          email: emailSent,
          otp: data.otp,
        },
      },
    });
  };

  const loading = requestOTPLoading || guestLoginLoading;

  return (
    <div className="bg-white max-h-[90vh] w-full xs:w-[564px] xs:min-h-[300px] px-8 py-11 xs:p-8 rounded-md flex flex-col gap-4 overflow-y-auto thin-scrollbar">
      <h2 className="font-semibold text-xl leading-tight m-0 text-[#141414]">
        {step === "email" ? "Login as guest" : "Enter verification code"}
      </h2>
      {step === "email" ? (
        <form
          className="flex flex-col justify-between flex-1"
          onSubmit={emailForm.handleSubmit(onEmailSubmit)}
        >
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="guest-email"
              className="text-sm font-medium text-[#141414]"
            >
              Email
            </label>
            <input
              id="guest-email"
              type="email"
              placeholder="you@example.com"
              className="w-full px-4 py-3 text-base leading-normal border border-[#E5E5E5] bg-[#FAFAFA] rounded-md text-[#141414] placeholder:text-[#808080] focus:outline-none focus:border-[#eead06]"
              {...emailForm.register("email")}
              autoComplete="email"
            />
            {emailForm.formState.errors.email && (
              <p className="text-[13px] text-[#e61919] m-0">
                {emailForm.formState.errors.email.message}
              </p>
            )}
          </div>
          <div className="flex justify-end mt-1">
            <Button
              type="submit"
              label="Send code"
              design="secondary"
              disabled={loading}
              loading={loading}
            >
              Send code
            </Button>
          </div>
        </form>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={otpForm.handleSubmit(onOtpSubmit)}
        >
          <p className="text-sm text-[#808080] m-0">Code sent to {emailSent}</p>
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="guest-otp"
              className="text-sm font-medium text-[#141414]"
            >
              Verification code
            </label>
            <input
              id="guest-otp"
              type="text"
              placeholder="Enter code"
              className="w-full px-4 py-3 text-base leading-normal border border-[#E5E5E5] bg-[#FAFAFA] rounded-md text-[#141414] placeholder:text-[#c56969] focus:outline-none focus:border-[#eead06]"
              {...otpForm.register("otp")}
              autoComplete="one-time-code"
              inputMode="numeric"
              autoFocus
            />
            {otpForm.formState.errors.otp && (
              <p className="text-[13px] text-[#e61919] m-0">
                {otpForm.formState.errors.otp.message}
              </p>
            )}
          </div>
          <div className="flex justify-end mt-1">
            <Button
              type="submit"
              label="Log in"
              design="secondary"
              disabled={loading || otpForm.formState.isValid === false}
              loading={loading}
            >
              Log in
            </Button>
          </div>
          <button
            type="button"
            className="bg-transparent border-0 p-0 text-sm text-[#629d94] cursor-pointer text-left underline hover:text-[#3b5f59]"
            onClick={() => setStep("email")}
          >
            Use a different email
          </button>
        </form>
      )}
    </div>
  );
};

export default LoginAsGuestModal;
